import React, { useState, useEffect, useMemo } from 'react';
import {
    BudgetFolder, BudgetItem, ResourceItem, ResourceProject, Task, PhysicalUnit, ApuItem, Level
} from '../src/types';
import { parseBudgetExcel, exportBudgetToExcel } from '../src/utils/budgetImporter';

// --- Context Menu State Type ---
interface ContextMenuState {
    visible: boolean;
    x: number;
    y: number;
    type: 'folder' | 'item' | 'apu_item' | null;
    targetId: string | null;
}

interface BudgetScreenProps {
    tasks: Task[];
    budgetTree: BudgetFolder[];
    setBudgetTree: React.Dispatch<React.SetStateAction<BudgetFolder[]>>;
    budgetItems: BudgetItem[];
    setBudgetItems: React.Dispatch<React.SetStateAction<BudgetItem[]>>;
    apuData: Record<string, ApuItem[]>;
    setApuData: React.Dispatch<React.SetStateAction<Record<string, ApuItem[]>>>;
    budgetResources: ResourceItem[];
    setBudgetResources: React.Dispatch<React.SetStateAction<ResourceItem[]>>;
    budgetProjects: ResourceProject[];
    setBudgetProjects: React.Dispatch<React.SetStateAction<ResourceProject[]>>;
    projectInfo: { name: string };
}

const BudgetScreen: React.FC<BudgetScreenProps> = ({
    tasks,
    budgetTree, setBudgetTree,
    budgetItems, setBudgetItems,
    apuData, setApuData,
    budgetResources: resources, setBudgetResources: setResources,
    budgetProjects: projects, setBudgetProjects: setProjects,
    projectInfo
}) => {
    // --- State ---
    const [mastersMenuOpen, setMastersMenuOpen] = useState(false);
    const [budgetMenuOpen, setBudgetMenuOpen] = useState(false);
    const [resourcesModalOpen, setResourcesModalOpen] = useState(false);
    const [isBudgetEditing, setIsBudgetEditing] = useState(false);

    // Sidebar Resizing
    const [sidebarWidth, setSidebarWidth] = useState(320);
    const [isResizing, setIsResizing] = useState(false);
    const sidebarRef = React.useRef<HTMLDivElement>(null);

    // Track selected folder ID separately for easier filtering
    const [activeFolderId, setActiveFolderId] = useState<string>('1-1-1');

    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
    const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);

    const [selectedBudgetItemId, setSelectedBudgetItemId] = useState<string | null>('item-1');

    // --- APU Data State ---
    const [isApuEditing, setIsApuEditing] = useState(false);

    // Add Resource to APU Modal State
    const [addApuResourceModalOpen, setAddApuResourceModalOpen] = useState(false);
    const [resourceSearchQuery, setResourceSearchQuery] = useState('');

    // --- Other Modals & UI State ---
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [folderModalMode, setFolderModalMode] = useState<'create_root' | 'create_sub' | 'edit'>('create_root');
    const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
    const [folderNameInput, setFolderNameInput] = useState('');

    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false, x: 0, y: 0, type: null, targetId: null
    });

    const [searchQuery, setSearchQuery] = useState('');
    const [isRenamingProject, setIsRenamingProject] = useState<string | null>(null);
    const [tempProjectName, setTempProjectName] = useState('');

    const [createResourceModalOpen, setCreateResourceModalOpen] = useState(false);
    const [newResourceData, setNewResourceData] = useState({
        code: '',
        description: '',
        type: 'Material',
        unit: 'un',
        costAccount: ''
    });

    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [editingItemMode, setEditingItemMode] = useState<string | null>(null); // Null for create, itemId for edit
    const [newItemData, setNewItemData] = useState({
        code: '',
        description: '',
        unit: 'm3',
        quantity: '',
        ganttTaskId: ''
    });

    // AI Modal State
    const [aiAssistantModalOpen, setAiAssistantModalOpen] = useState(false);
    const [isAiProcessing, setIsAiProcessing] = useState(false);

    // File Input Ref
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // --- Effects ---
    useEffect(() => {
        const handleClick = () => setContextMenu({ ...contextMenu, visible: false });
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [contextMenu]);

    // Resize Handler
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isResizing) {
                const newWidth = e.clientX;
                const maxWidth = window.innerWidth * 0.5;
                if (newWidth >= 200 && newWidth <= maxWidth) {
                    setSidebarWidth(newWidth);
                }
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.cursor = 'default';
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    // --- Helpers ---
    const formatMoney = (value: number) => {
        return value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // --- Actions: Budget Tree ---
    const toggleFolder = (folderId: string) => {
        const toggleRecursive = (list: BudgetFolder[]): BudgetFolder[] => {
            return list.map(item => {
                if (item.id === folderId) return { ...item, expanded: !item.expanded };
                if (item.children.length > 0) return { ...item, children: toggleRecursive(item.children) };
                return item;
            });
        };
        setBudgetTree(toggleRecursive(budgetTree));
    };

    const selectFolder = (folderId: string) => {
        setActiveFolderId(folderId);
        const selectRecursive = (list: BudgetFolder[]): BudgetFolder[] => {
            return list.map(item => ({
                ...item,
                isSelected: item.id === folderId,
                children: selectRecursive(item.children)
            }));
        };
        setBudgetTree(selectRecursive(budgetTree));
    };

    // Generic Context Menu Handler
    const handleContextMenu = (e: React.MouseEvent, type: 'folder' | 'item' | 'apu_item', id: string) => {
        // Validation rules for showing context menu
        if (type === 'item' && !isBudgetEditing) return; // Only show item menu if editing budget
        if (type === 'apu_item' && !isApuEditing) return; // Only show apu menu if editing apu

        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type,
            targetId: id
        });
    };

    // Modal Handlers (Folders)
    const openCreateRootFolder = () => {
        setFolderModalMode('create_root');
        setFolderNameInput('');
        setFolderModalOpen(true);
    };

    const openCreateSubFolder = () => {
        if (!contextMenu.targetId) return;
        setFolderModalMode('create_sub');
        setTargetFolderId(contextMenu.targetId);
        setFolderNameInput('');
        setFolderModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const openEditFolder = () => {
        if (!contextMenu.targetId) return;
        setFolderModalMode('edit');
        setTargetFolderId(contextMenu.targetId);
        const findName = (list: BudgetFolder[]): string => {
            for (const item of list) {
                if (item.id === contextMenu.targetId) return item.name;
                const found = findName(item.children);
                if (found) return found;
            }
            return '';
        };
        setFolderNameInput(findName(budgetTree));
        setFolderModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleSaveFolder = () => {
        if (!folderNameInput.trim()) return;
        if (folderModalMode === 'create_root') {
            const newFolder: BudgetFolder = {
                id: Math.random().toString(36).substr(2, 9),
                name: folderNameInput,
                children: [],
                expanded: true
            };
            setBudgetTree([...budgetTree, newFolder]);
        } else if (folderModalMode === 'create_sub' && targetFolderId) {
            const addRecursive = (list: BudgetFolder[]): BudgetFolder[] => {
                return list.map(item => {
                    if (item.id === targetFolderId) {
                        return {
                            ...item,
                            expanded: true,
                            children: [...item.children, {
                                id: Math.random().toString(36).substr(2, 9),
                                name: folderNameInput,
                                children: [],
                                expanded: true
                            }]
                        };
                    }
                    if (item.children.length > 0) return { ...item, children: addRecursive(item.children) };
                    return item;
                });
            };
            setBudgetTree(addRecursive(budgetTree));
        } else if (folderModalMode === 'edit' && targetFolderId) {
            const editRecursive = (list: BudgetFolder[]): BudgetFolder[] => {
                return list.map(item => {
                    if (item.id === targetFolderId) return { ...item, name: folderNameInput };
                    if (item.children.length > 0) return { ...item, children: editRecursive(item.children) };
                    return item;
                });
            };
            setBudgetTree(editRecursive(budgetTree));
        }
        setFolderModalOpen(false);
    };

    // --- Actions: Budget Item Management ---
    const handleOpenAddItem = () => {
        setEditingItemMode(null);
        setNewItemData({ code: '', description: '', unit: 'm3', quantity: '', ganttTaskId: '' });
        setAddItemModalOpen(true);
    };

    const handleEditItem = () => {
        if (!contextMenu.targetId) return;
        const itemToEdit = budgetItems.find(i => i.id === contextMenu.targetId);
        if (itemToEdit) {
            setEditingItemMode(itemToEdit.id);
            setNewItemData({
                code: itemToEdit.code,
                description: itemToEdit.description,
                unit: itemToEdit.unit,
                quantity: itemToEdit.quantity.toString(),
                ganttTaskId: itemToEdit.ganttTaskId || ''
            });
            setAddItemModalOpen(true);
        }
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleDeleteItem = () => {
        if (!contextMenu.targetId) return;
        if (confirm("¿Estás seguro de eliminar este ítem?")) {
            setBudgetItems(prev => prev.filter(i => i.id !== contextMenu.targetId));
            if (selectedBudgetItemId === contextMenu.targetId) setSelectedBudgetItemId(null);
        }
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleSaveItem = () => {
        if (!newItemData.code || !newItemData.description || !newItemData.quantity) {
            alert("Por favor complete todos los campos requeridos.");
            return;
        }

        if (editingItemMode) {
            // Edit existing
            setBudgetItems(prev => prev.map(item => {
                if (item.id === editingItemMode) {
                    return {
                        ...item,
                        code: newItemData.code,
                        description: newItemData.description,
                        unit: newItemData.unit,
                        quantity: parseFloat(newItemData.quantity),
                        ganttTaskId: newItemData.ganttTaskId
                    };
                }
                return item;
            }));
        } else {
            // Create new
            const newItem: BudgetItem = {
                id: `item-${Math.random().toString(36).substr(2, 9)}`,
                folderId: activeFolderId,
                code: newItemData.code,
                description: newItemData.description,
                unit: newItemData.unit,
                quantity: parseFloat(newItemData.quantity),
                unitPrice: 0,
                ganttTaskId: newItemData.ganttTaskId
            };
            setBudgetItems([...budgetItems, newItem]);
        }

        setAddItemModalOpen(false);
    };

    // --- Actions: APU Management ---

    const handleEditApuToggle = () => {
        if (isApuEditing) {
            // Save logic
            if (selectedBudgetItemId) {
                const currentApuItems = apuData[selectedBudgetItemId] || [];
                const newTotalUnitPrice = currentApuItems.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0);

                // Update the main budget item with the new total unit price
                setBudgetItems(prev => prev.map(item =>
                    item.id === selectedBudgetItemId
                        ? { ...item, unitPrice: newTotalUnitPrice }
                        : item
                ));
            }
            setIsApuEditing(false);
        } else {
            // Start editing
            setIsApuEditing(true);
        }
    };

    const handleApuInputChange = (apuId: string, field: 'quantity' | 'unitPrice', value: string) => {
        if (!selectedBudgetItemId) return;
        const numValue = parseFloat(value) || 0;

        setApuData(prev => ({
            ...prev,
            [selectedBudgetItemId]: prev[selectedBudgetItemId].map(item =>
                item.id === apuId ? { ...item, [field]: numValue } : item
            )
        }));
    };

    const handleOpenAddApuResource = () => {
        setResourceSearchQuery('');
        setAddApuResourceModalOpen(true);
    };

    const handleAddResourceToApu = (resource: ResourceItem) => {
        if (!selectedBudgetItemId) return;

        const newApuItem: ApuItem = {
            id: `apu-${Math.random().toString(36).substr(2, 9)}`,
            resourceId: resource.id,
            quantity: 1, // Default
            unitPrice: 0 // Should probably pull from resource cost if available, default 0 for now
        };

        setApuData(prev => ({
            ...prev,
            [selectedBudgetItemId]: [...(prev[selectedBudgetItemId] || []), newApuItem]
        }));

        setAddApuResourceModalOpen(false);
    };

    const handleDeleteApuItem = (apuId: string) => {
        if (!selectedBudgetItemId) return;
        setApuData(prev => ({
            ...prev,
            [selectedBudgetItemId]: prev[selectedBudgetItemId].filter(i => i.id !== apuId)
        }));
    };

    const handleDeleteApuItemContext = () => {
        if (contextMenu.targetId) {
            handleDeleteApuItem(contextMenu.targetId);
        }
        setContextMenu({ ...contextMenu, visible: false });
    };

    // --- Actions: AI Assistant ---
    const handleAiAction = async (action: 'analyze' | 'distribute' | 'omissions') => {
        setIsAiProcessing(true);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        switch (action) {
            case 'analyze':
                alert("Análisis de IA: Se han detectado 3 partidas con precios unitarios por encima del promedio del mercado.");
                break;
            case 'distribute':
                alert("Distribución de IA: Se ha generado una propuesta de distribución de presupuesto basada en históricos de proyectos similares.");
                break;
            case 'omissions':
                alert("Detección de Omisiones: La IA sugiere añadir la partida 'Limpieza Final de Obra' que suele omitirse en este tipo de proyectos.");
                break;
        }

        setIsAiProcessing(false);
        setAiAssistantModalOpen(false);
    };

    // --- Import / Export Handlers ---
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            try {
                const { tree, items } = await parseBudgetExcel(e.target.files[0]);
                if (confirm(`Se encontraron ${items.length} ítems y una estructura de carpetas. ¿Desea reemplazar el presupuesto actual?`)) {
                    setBudgetTree(tree);
                    setBudgetItems(items);
                    alert("Presupuesto importado correctamente.");
                }
            } catch (error) {
                alert(error);
            }
            // Reset input
            e.target.value = '';
        }
    };

    // --- Actions: Resources (Masters) ---
    const handleAddProject = () => {
        const name = prompt("Nombre de la nueva Obra/Carpeta:");
        if (name) {
            setProjects([...projects, { id: Math.random().toString(36).substr(2, 9), name }]);
        }
    };

    const handleEditProjectStart = (p: ResourceProject) => {
        setIsRenamingProject(p.id);
        setTempProjectName(p.name);
    };

    const handleEditProjectSave = () => {
        if (isRenamingProject && tempProjectName.trim()) {
            setProjects(prev => prev.map(p => p.id === isRenamingProject ? { ...p, name: tempProjectName } : p));
            setIsRenamingProject(null);
        }
    };

    const handleDeleteProject = () => {
        if (!selectedProjectId) return;
        if (confirm("¿Eliminar esta carpeta y todos sus recursos asociados?")) {
            setProjects(prev => prev.filter(p => p.id !== selectedProjectId));
            setResources(prev => prev.filter(r => r.projectId !== selectedProjectId));
            setSelectedProjectId(null);
        }
    };

    const handleAddResourceClick = () => {
        if (!selectedProjectId) return;
        setNewResourceData({ code: '', description: '', type: 'Material', unit: 'un', costAccount: '' });
        setCreateResourceModalOpen(true);
    };

    const handleSaveNewResource = () => {
        if (!selectedProjectId) return;
        if (!newResourceData.code || !newResourceData.description || !newResourceData.costAccount) {
            alert("Por favor complete todos los campos obligatorios.");
            return;
        }
        const newResource: ResourceItem = {
            id: Math.random().toString(36).substr(2, 9),
            projectId: selectedProjectId,
            ...newResourceData
        };
        setResources([...resources, newResource]);
        setCreateResourceModalOpen(false);
    };

    const handleEditResource = () => {
        if (!selectedResourceId) return;
        const res = resources.find(r => r.id === selectedResourceId);
        if (!res) return;
        const newDesc = prompt("Editar Descripción:", res.description);
        if (newDesc) {
            setResources(prev => prev.map(r => r.id === selectedResourceId ? { ...r, description: newDesc } : r));
        }
    };

    const handleDeleteResource = () => {
        if (!selectedResourceId) return;
        if (confirm("¿Eliminar el recurso seleccionado?")) {
            setResources(prev => prev.filter(r => r.id !== selectedResourceId));
            setSelectedResourceId(null);
        }
    };

    // --- Derived Data ---
    const currentItems = budgetItems.filter(item => item.folderId === activeFolderId);
    const filteredResources = resources.filter(r =>
        r.projectId === selectedProjectId &&
        (r.description.toLowerCase().includes(searchQuery.toLowerCase()) || r.code.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    const selectedBudgetItem = budgetItems.find(i => i.id === selectedBudgetItemId);

    // Get APU items for current selection
    const currentApuItems = selectedBudgetItemId ? (apuData[selectedBudgetItemId] || []) : [];

    // Filter resources for APU Modal
    const filteredApuResources = resources.filter(r =>
        r.description.toLowerCase().includes(resourceSearchQuery.toLowerCase()) ||
        r.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Flatten tasks for Gantt dropdown
    const allGanttTasks = useMemo(() => {
        const flatten = (list: Task[]): Task[] => {
            let res: Task[] = [];
            list.forEach(t => {
                if (t.type === 'task') {
                    res.push(t);
                }
                if (t.children && t.children.length > 0) {
                    res = [...res, ...flatten(t.children)];
                }
            });
            return res;
        };
        return flatten(tasks);
    }, [tasks]);

    // --- Renderers ---

    const renderBudgetTree = (nodes: BudgetFolder[], level = 0) => {
        return nodes.map(node => (
            <div key={node.id} className="select-none">
                <div
                    className={`
                        flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm
                        ${node.isSelected ? 'bg-blue-100 text-primary font-bold dark:bg-blue-900/30' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}
                    `}
                    style={{ paddingLeft: `${level * 16 + 8}px` }}
                    onClick={() => selectFolder(node.id)}
                    onContextMenu={(e) => handleContextMenu(e, 'folder', node.id)}
                >
                    {node.children.length > 0 ? (
                        <span
                            className="material-symbols-outlined text-[18px] text-slate-400 hover:text-slate-600 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); toggleFolder(node.id); }}
                        >
                            {node.expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                        </span>
                    ) : (
                        // Spacer for alignment if no children (leaf folder)
                        <span className="w-[18px] h-[18px] flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                        </span>
                    )}

                    {/* Icon based on state */}
                    <span className={`material-symbols-outlined text-[18px] ${node.children.length === 0 ? 'text-slate-400' : 'text-blue-400'}`}>
                        {node.children.length > 0 ? (node.expanded ? 'folder_open' : 'folder') : 'folder'}
                    </span>
                    <span className="truncate">{node.name}</span>
                </div>
                {node.expanded && node.children.length > 0 && (
                    <div>{renderBudgetTree(node.children, level + 1)}</div>
                )}
            </div>
        ));
    };

    return (
        <div className="flex flex-col h-full bg-background-light dark:bg-background-dark overflow-hidden">

            {/* Top Blue Bar (Franja Azul) */}
            <div className="bg-[#1a73e8] dark:bg-blue-900 text-white h-12 px-6 flex items-center shrink-0 shadow-md z-30 relative gap-4">
                <div className="relative">
                    <button
                        onClick={() => { setMastersMenuOpen(!mastersMenuOpen); setBudgetMenuOpen(false); }}
                        className={`flex items-center gap-1 text-sm font-bold px-3 py-1.5 rounded transition-colors ${mastersMenuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                    >
                        Maestros
                        <span className="material-symbols-outlined text-[18px]">expand_more</span>
                    </button>

                    {mastersMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-40" onClick={() => setMastersMenuOpen(false)}></div>
                            <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                <button
                                    onClick={() => { setMastersMenuOpen(false); setResourcesModalOpen(true); }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">inventory_2</span>
                                    Recursos
                                </button>
                                <button
                                    onClick={() => { setMastersMenuOpen(false); /* Handle Units */ }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">straighten</span>
                                    Unidades
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="relative">
                    <button
                        onClick={() => { setBudgetMenuOpen(!budgetMenuOpen); setMastersMenuOpen(false); }}
                        className={`flex items-center gap-1 text-sm font-bold px-3 py-1.5 rounded transition-colors ${budgetMenuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                    >
                        Presupuesto
                        <span className="material-symbols-outlined text-[18px]">expand_more</span>
                    </button>

                    {budgetMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-40" onClick={() => setBudgetMenuOpen(false)}></div>
                            <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                <button
                                    onClick={() => { setBudgetMenuOpen(false); alert("Módulo Modificación de Presupuesto en construcción"); }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">edit_note</span>
                                    Modificación de Presupuesto
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Existing Budget Content */}
            <div className="flex flex-1 overflow-hidden h-full">
                <aside
                    ref={sidebarRef}
                    style={{ width: sidebarWidth }}
                    className="bg-white dark:bg-slate-900 border-r border-border-light dark:border-border-dark flex flex-col overflow-hidden hidden md:flex relative shrink-0"
                >
                    {/* Resizer Handle */}
                    <div
                        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/50 z-50 transition-colors"
                        onMouseDown={() => setIsResizing(true)}
                    ></div>

                    <div className="p-4 border-b border-border-light dark:border-border-dark">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-slate-400 text-lg">apartment</span>
                                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">{projectInfo.name}</h3>
                            </div>
                            <button
                                onClick={() => setIsBudgetEditing(!isBudgetEditing)}
                                className={`p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${isBudgetEditing ? 'text-primary' : 'text-slate-400'}`}
                                title={isBudgetEditing ? "Terminar Edición" : "Editar Presupuesto"}
                            >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                            </button>
                        </div>
                    </div>

                    {/* Create Folder Button (Only visible when editing) */}
                    {isBudgetEditing && (
                        <div className="px-4 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                            <button
                                onClick={openCreateRootFolder}
                                className="w-full flex items-center justify-center gap-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-xs font-bold transition-colors"
                            >
                                <span className="material-symbols-outlined text-[16px]">create_new_folder</span>
                                Crear Carpeta
                            </button>
                        </div>
                    )}

                    {/* Import/Export Buttons (Only visible when editing) */}
                    {isBudgetEditing && (
                        <div className="px-4 pb-2 animate-in fade-in slide-in-from-top-2 duration-200 flex flex-col gap-2">
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full flex items-center justify-center gap-1 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 text-green-700 dark:text-green-400 py-1.5 rounded border border-green-200 dark:border-green-800 text-xs font-bold transition-colors"
                            >
                                <span className="material-symbols-outlined text-[16px]">upload_file</span>
                                Importar Excel
                            </button>
                            <button
                                onClick={() => exportBudgetToExcel(budgetTree, budgetItems)}
                                className="w-full flex items-center justify-center gap-1 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-xs font-bold transition-colors"
                            >
                                <span className="material-symbols-outlined text-[16px]">download</span>
                                Exportar Presupuesto
                            </button>
                            <button
                                onClick={() => setAiAssistantModalOpen(true)}
                                className="w-full flex items-center justify-center gap-1 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 py-1.5 rounded border border-indigo-200 dark:border-indigo-800 text-xs font-bold transition-colors"
                            >
                                <span className="material-symbols-outlined text-[16px]">smart_toy</span>
                                Asistente de IA
                            </button>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-2">
                        {renderBudgetTree(budgetTree)}
                    </div>
                </aside>
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-background-light dark:bg-background-dark">
                    <section className="h-[55%] flex flex-col min-h-[300px] border-b border-border-light dark:border-border-dark bg-white dark:bg-slate-900 shadow-sm relative z-10">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
                            <div>
                                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1">
                                    <span>Costo Directo</span>
                                    <span className="material-symbols-outlined text-[10px]">chevron_right</span>
                                    <span>Obra Gruesa</span>
                                </div>
                                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Presupuesto Fundaciones</h1>
                            </div>
                            <div className="flex items-center gap-3">
                                {isBudgetEditing && (
                                    <button
                                        onClick={handleOpenAddItem}
                                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-blue-600 transition-colors shadow-sm shadow-blue-500/30 animate-in fade-in zoom-in duration-200"
                                    >
                                        <span className="material-symbols-outlined text-lg">add</span>
                                        Agregar Ítem
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 w-24">Código</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">Descripción</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">Partida Gantt</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 text-right w-20">Unidad</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 text-right w-24">Cantidad</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 text-right w-32">Valor Unitario</th>
                                        <th className="px-6 py-3 font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 text-right w-32">Valor Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                                    {currentItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="p-8 text-center text-slate-400 italic">Esta carpeta está vacía.</td>
                                        </tr>
                                    ) : (
                                        currentItems.map((item) => {
                                            const total = item.quantity * item.unitPrice;
                                            const isSelected = selectedBudgetItemId === item.id;
                                            return (
                                                <tr
                                                    key={item.id}
                                                    onClick={() => setSelectedBudgetItemId(item.id)}
                                                    onContextMenu={(e) => handleContextMenu(e, 'item', item.id)}
                                                    className={`
                                                        hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer group 
                                                        ${isSelected ? 'bg-primary/5 dark:bg-primary/10 border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'}
                                                    `}
                                                >
                                                    <td className="px-6 py-3 font-mono text-xs text-primary font-medium">{item.code}</td>
                                                    <td className="px-6 py-3 font-medium text-slate-900 dark:text-slate-100">{item.description}</td>
                                                    <td className="px-6 py-3 text-xs text-slate-500 italic">
                                                        {allGanttTasks.find(t => t.id === item.ganttTaskId)?.name || '-'}
                                                    </td>
                                                    <td className="px-6 py-3 text-right text-slate-600 dark:text-slate-400">{item.unit}</td>
                                                    <td className="px-6 py-3 text-right text-slate-700 dark:text-slate-300 font-mono">{item.quantity.toLocaleString()}</td>
                                                    <td className="px-6 py-3 text-right text-slate-600 dark:text-slate-400 font-mono">${formatMoney(item.unitPrice)}</td>
                                                    <td className="px-6 py-3 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">${formatMoney(total)}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* APU Section */}
                    <section className="flex-1 flex flex-col bg-white dark:bg-slate-900 min-h-0 relative">
                        <div className="flex items-center justify-between px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30">
                            <div className="flex items-center gap-3">
                                <div className="size-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                    <span className="material-symbols-outlined text-[18px]">analytics</span>
                                </div>
                                <div>
                                    <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Análisis de Precio Unitario (APU)</div>
                                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                        {selectedBudgetItem ? `${selectedBudgetItem.code}: ${selectedBudgetItem.description}` : 'Seleccione un ítem'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 items-center">
                                {isBudgetEditing && selectedBudgetItemId && (
                                    <>
                                        {isApuEditing ? (
                                            <>
                                                <button
                                                    onClick={handleOpenAddApuResource}
                                                    className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs font-bold hover:bg-indigo-700 flex items-center gap-1 shadow-sm animate-in fade-in zoom-in duration-200"
                                                >
                                                    <span className="material-symbols-outlined text-sm">add_circle</span> Añadir Recurso
                                                </button>
                                                <div className="h-6 w-px bg-slate-300 dark:bg-slate-700 mx-2"></div>
                                                <button
                                                    onClick={handleEditApuToggle}
                                                    className="px-4 py-1.5 bg-green-600 text-white rounded text-xs font-bold hover:bg-green-700 flex items-center gap-1 shadow-sm animate-in fade-in zoom-in duration-200"
                                                >
                                                    <span className="material-symbols-outlined text-sm">save</span> Guardar APU
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={handleEditApuToggle}
                                                className="text-xs font-medium text-primary hover:text-blue-700 flex items-center gap-1 animate-in fade-in zoom-in duration-200"
                                            >
                                                <span className="material-symbols-outlined text-sm">edit</span> Editar APU
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-4 bg-slate-50 dark:bg-slate-950/20">
                            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                        <tr>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Código</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Recurso</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Tipo</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right w-20">Unidad</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right w-24">Cantidad</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right w-32">Valor Unitario</th>
                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right w-32">Valor Total</th>
                                            {isApuEditing && <th className="w-10"></th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {currentApuItems.length === 0 ? (
                                            <tr>
                                                <td colSpan={isApuEditing ? 8 : 7} className="p-8 text-center text-slate-400 italic">
                                                    APU sin recursos asignados.
                                                </td>
                                            </tr>
                                        ) : (
                                            currentApuItems.map((apuItem) => {
                                                const res = resources.find(r => r.id === apuItem.resourceId);
                                                const total = apuItem.quantity * apuItem.unitPrice;
                                                return (
                                                    <tr
                                                        key={apuItem.id}
                                                        onContextMenu={(e) => handleContextMenu(e, 'apu_item', apuItem.id)}
                                                        className="hover:bg-slate-50 dark:hover:bg-slate-800"
                                                    >
                                                        <td className="px-4 py-2 font-mono text-xs text-slate-500">{res?.code || '-'}</td>
                                                        <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{res?.description || 'Recurso desconocido'}</td>
                                                        <td className="px-4 py-2 text-xs text-slate-500">{res?.type}</td>
                                                        <td className="px-4 py-2 text-right text-slate-500">{res?.unit}</td>

                                                        {/* Quantity Input or Text */}
                                                        <td className="px-4 py-2 text-right font-mono">
                                                            {isApuEditing ? (
                                                                <input
                                                                    type="number"
                                                                    className="w-full text-right border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-sm bg-white dark:bg-slate-800 focus:ring-1 focus:ring-primary outline-none"
                                                                    value={apuItem.quantity}
                                                                    onChange={(e) => handleApuInputChange(apuItem.id, 'quantity', e.target.value)}
                                                                />
                                                            ) : (
                                                                <span className="text-slate-800 dark:text-slate-200">{apuItem.quantity}</span>
                                                            )}
                                                        </td>

                                                        {/* Unit Price Input or Text */}
                                                        <td className="px-4 py-2 text-right font-mono">
                                                            {isApuEditing ? (
                                                                <input
                                                                    type="number"
                                                                    className="w-full text-right border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-sm bg-white dark:bg-slate-800 focus:ring-1 focus:ring-primary outline-none"
                                                                    value={apuItem.unitPrice}
                                                                    onChange={(e) => handleApuInputChange(apuItem.id, 'unitPrice', e.target.value)}
                                                                />
                                                            ) : (
                                                                <span className="text-slate-800 dark:text-slate-200 font-medium">${formatMoney(apuItem.unitPrice)}</span>
                                                            )}
                                                        </td>

                                                        <td className="px-4 py-2 text-right font-mono text-slate-900 dark:text-slate-100 font-bold">
                                                            ${formatMoney(total)}
                                                        </td>
                                                        {isApuEditing && (
                                                            <td className="px-2 py-2 text-center">
                                                                <button onClick={() => handleDeleteApuItem(apuItem.id)} className="text-slate-400 hover:text-red-500">
                                                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        )}
                                        {currentApuItems.length > 0 && (
                                            <tr className="bg-slate-50 dark:bg-slate-800/50 font-bold border-t border-slate-200 dark:border-slate-700">
                                                <td colSpan={6} className="px-4 py-2 text-right text-slate-600 dark:text-slate-400 uppercase text-xs">Total Costo Unitario:</td>
                                                <td className="px-4 py-2 text-right font-mono text-primary text-sm">
                                                    ${formatMoney(currentApuItems.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0))}
                                                </td>
                                                {isApuEditing && <td></td>}
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Context Menu */}
            {contextMenu.visible && (
                <div
                    className="fixed z-50 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-48 animate-in fade-in zoom-in duration-75"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.type === 'folder' && (
                        <>
                            <button
                                onClick={openEditFolder}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                                Editar Carpeta
                            </button>
                            <button
                                onClick={openCreateSubFolder}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
                                Crear Subcarpeta
                            </button>
                        </>
                    )}

                    {contextMenu.type === 'item' && (
                        <>
                            <button
                                onClick={handleEditItem}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                                Editar
                            </button>
                            <button
                                onClick={handleDeleteItem}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                Eliminar
                            </button>
                        </>
                    )}

                    {contextMenu.type === 'apu_item' && (
                        <button
                            onClick={handleDeleteApuItemContext}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                            Eliminar Recurso
                        </button>
                    )}
                </div>
            )}

            {/* Folder Modal (Create/Edit) */}
            {folderModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-sm border border-slate-200 dark:border-slate-800 p-6">
                        <h3 className="text-lg font-bold mb-4 text-slate-800 dark:text-white">
                            {folderModalMode === 'edit' ? 'Editar Carpeta' : folderModalMode === 'create_sub' ? 'Nueva Subcarpeta' : 'Nueva Carpeta'}
                        </h3>
                        <input
                            type="text"
                            autoFocus
                            placeholder="Nombre de la carpeta"
                            className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none mb-4"
                            value={folderNameInput}
                            onChange={(e) => setFolderNameInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveFolder()}
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setFolderModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded text-sm font-medium">Cancelar</button>
                            <button onClick={handleSaveFolder} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm font-medium shadow-sm">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Item Modal */}
            {addItemModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                {editingItemMode ? 'Editar Ítem' : 'Agregar Nuevo Ítem'}
                            </h3>
                            <button onClick={() => setAddItemModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Código</label>
                                <input
                                    type="text"
                                    autoFocus
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    value={newItemData.code}
                                    onChange={(e) => setNewItemData({ ...newItemData, code: e.target.value })}
                                    placeholder="Ej: STR-003"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descripción</label>
                                <input
                                    type="text"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    value={newItemData.description}
                                    onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })}
                                    placeholder="Descripción del ítem"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Unidad</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                        value={newItemData.unit}
                                        onChange={(e) => setNewItemData({ ...newItemData, unit: e.target.value })}
                                    >
                                        {['m3', 'm2', 'ml', 'un', 'gl', 'kg', 'ton', 'ltr'].map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cantidad</label>
                                    <input
                                        type="number"
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                        value={newItemData.quantity}
                                        onChange={(e) => setNewItemData({ ...newItemData, quantity: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Partida Carta Gantt</label>
                                <select
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    value={newItemData.ganttTaskId}
                                    onChange={(e) => setNewItemData({ ...newItemData, ganttTaskId: e.target.value })}
                                >
                                    <option value="">Seleccione una partida...</option>
                                    {allGanttTasks.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setAddItemModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded text-sm font-medium">Cancelar</button>
                            <button onClick={handleSaveItem} className="px-6 py-2 bg-primary text-white rounded hover:bg-blue-600 text-sm font-bold shadow-sm">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* NEW ADD RESOURCE TO APU MODAL */}
            {addApuResourceModalOpen && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Añadir Recurso al APU</h3>
                            <button onClick={() => setAddApuResourceModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>

                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
                                <input
                                    type="text"
                                    placeholder="Buscar recursos por código o nombre..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-primary outline-none"
                                    value={resourceSearchQuery}
                                    onChange={(e) => setResourceSearchQuery(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-24">Código</th>
                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700">Descripción</th>
                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-24">Tipo</th>
                                        <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-20 text-center">Unidad</th>
                                        <th className="w-16 border-b border-slate-200 dark:border-slate-700"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {filteredApuResources.length === 0 ? (
                                        <tr><td colSpan={5} className="p-8 text-center text-slate-400">No se encontraron recursos.</td></tr>
                                    ) : (
                                        filteredApuResources.map(res => (
                                            <tr key={res.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 group">
                                                <td className="px-4 py-3 text-sm font-mono text-slate-600 dark:text-slate-400">{res.code}</td>
                                                <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">{res.description}</td>
                                                <td className="px-4 py-3 text-sm text-slate-500">{res.type}</td>
                                                <td className="px-4 py-3 text-sm text-center text-slate-500">{res.unit}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={() => handleAddResourceToApu(res)}
                                                        className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded text-xs font-bold transition-colors shadow-sm border border-blue-100"
                                                    >
                                                        Añadir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MASTERS / RESOURCES MODAL (Existing) --- */}
            {resourcesModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-[90%] max-w-7xl border border-slate-300 dark:border-slate-700 flex flex-col h-[85vh] overflow-hidden">

                        {/* Header */}
                        <div className="flex justify-between items-center p-4 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary text-[24px]">inventory_2</span>
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white uppercase">Recursos</h3>
                            </div>
                            <button onClick={() => setResourcesModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                <span className="material-symbols-outlined text-[24px]">close</span>
                            </button>
                        </div>

                        {/* Body - Split View */}
                        <div className="flex flex-1 overflow-hidden">

                            {/* Left Pane: Project Folders (Obras) */}
                            <div className="w-1/4 min-w-[250px] bg-slate-50 dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-700 flex flex-col">
                                <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Obras</span>
                                    <div className="flex gap-1">
                                        <button onClick={handleAddProject} className="p-1 text-slate-500 hover:text-primary hover:bg-slate-200 dark:hover:bg-slate-800 rounded" title="Añadir Obra">
                                            <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
                                        </button>
                                        <button
                                            onClick={handleDeleteProject}
                                            disabled={!selectedProjectId}
                                            className="p-1 text-slate-500 hover:text-red-500 hover:bg-slate-200 dark:hover:bg-slate-800 rounded disabled:opacity-30"
                                            title="Eliminar Obra"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                    {projects.map(p => (
                                        <div
                                            key={p.id}
                                            onClick={() => setSelectedProjectId(p.id)}
                                            className={`
                                                flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors group
                                                ${selectedProjectId === p.id
                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}
                                            `}
                                        >
                                            <span className={`material-symbols-outlined text-[20px] ${selectedProjectId === p.id ? 'text-blue-500' : 'text-slate-400'}`}>folder</span>
                                            {isRenamingProject === p.id ? (
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={tempProjectName}
                                                    onChange={(e) => setTempProjectName(e.target.value)}
                                                    onBlur={handleEditProjectSave}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleEditProjectSave()}
                                                    className="w-full bg-white border border-blue-300 rounded px-1 py-0 text-sm outline-none"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <span className="flex-1 truncate">{p.name}</span>
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleEditProjectStart(p); }}
                                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">edit</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Pane: Resources Content */}
                            <div className="flex-1 flex flex-col bg-white dark:bg-slate-900">

                                {/* Toolbar for Resources */}
                                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleAddResourceClick}
                                            disabled={!selectedProjectId}
                                            className="flex items-center gap-1 bg-[#00b050] hover:bg-[#009040] text-white px-3 py-1.5 rounded-md text-sm font-bold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            +A
                                        </button>
                                        <button
                                            onClick={handleEditResource}
                                            disabled={!selectedResourceId}
                                            className="flex items-center gap-1 bg-[#ffc000] hover:bg-[#e0a800] text-white px-3 py-1.5 rounded-md text-sm font-bold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Ed <span className="material-symbols-outlined text-[16px]">edit</span>
                                        </button>
                                        <button
                                            onClick={handleDeleteResource}
                                            disabled={!selectedResourceId}
                                            className="flex items-center gap-1 bg-slate-100 border border-slate-300 hover:bg-red-50 text-slate-700 hover:text-red-600 px-3 py-1.5 rounded-md text-sm font-bold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            EL <span className="material-symbols-outlined text-[18px] text-red-600 font-bold">remove</span>
                                        </button>
                                    </div>

                                    <div className="relative flex-1 max-w-md">
                                        <span className="material-symbols-outlined absolute left-2 top-1.5 text-slate-400 text-[20px]">search</span>
                                        <input
                                            type="text"
                                            placeholder="Buscar recursos..."
                                            className="w-full pl-9 pr-4 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm focus:ring-2 focus:ring-primary outline-none"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            disabled={!selectedProjectId}
                                        />
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="flex-1 overflow-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 shadow-sm">
                                            <tr>
                                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-32">Código</th>
                                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700">Descripción</th>
                                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-32">Tipo Recurso</th>
                                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-24 text-center">Unidad</th>
                                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700 w-32 text-right">Cuenta Costo</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {!selectedProjectId ? (
                                                <tr>
                                                    <td colSpan={5} className="p-10 text-center text-slate-400 italic">
                                                        <div className="flex flex-col items-center gap-2">
                                                            <span className="material-symbols-outlined text-4xl opacity-30">folder_open</span>
                                                            Seleccione una carpeta de obra para ver sus recursos.
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : filteredResources.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="p-10 text-center text-slate-400 italic">
                                                        No hay recursos en esta obra. Utilice el botón "+A" para añadir uno.
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredResources.map(r => (
                                                    <tr
                                                        key={r.id}
                                                        onClick={() => setSelectedResourceId(r.id)}
                                                        className={`
                                                            cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/10
                                                            ${selectedResourceId === r.id ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
                                                        `}
                                                    >
                                                        <td className="px-4 py-2 text-sm font-mono text-slate-600 dark:text-slate-400">{r.code}</td>
                                                        <td className="px-4 py-2 text-sm font-medium text-slate-800 dark:text-white">{r.description}</td>
                                                        <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">
                                                            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-xs border border-slate-200 dark:border-slate-600">{r.type}</span>
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-center text-slate-600 dark:text-slate-400">{r.unit}</td>
                                                        <td className="px-4 py-2 text-sm text-right font-mono text-slate-500">{r.costAccount}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-500 text-right">
                                    Total: {filteredResources.length} recursos
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* NEW ADD RESOURCE MODAL */}
            {createResourceModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6 flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">add_circle</span>
                                Nuevo Recurso
                            </h3>
                            <button onClick={() => setCreateResourceModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Código de Recurso</label>
                                <input
                                    type="text"
                                    autoFocus
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    placeholder="Ej: MAT-001"
                                    value={newResourceData.code}
                                    onChange={(e) => setNewResourceData({ ...newResourceData, code: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Descripción de Recurso</label>
                                <input
                                    type="text"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    placeholder="Ej: Cemento Portland"
                                    value={newResourceData.description}
                                    onChange={(e) => setNewResourceData({ ...newResourceData, description: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Tipo de Recurso</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                        value={newResourceData.type}
                                        onChange={(e) => setNewResourceData({ ...newResourceData, type: e.target.value })}
                                    >
                                        {['Material', 'MOD', 'Subcontrato', 'Herramienta', 'Equipo', 'Fletes', 'Global'].map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Unidad</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                        value={newResourceData.unit}
                                        onChange={(e) => setNewResourceData({ ...newResourceData, unit: e.target.value })}
                                    >
                                        {['un', 'gl', 'ml', 'm2', 'm3', 'kg', 'ton', 'ltr', 'dia', 'mes'].map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Cuenta de Costo</label>
                                <input
                                    type="number"
                                    className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none"
                                    placeholder="Ej: 2005"
                                    value={newResourceData.costAccount}
                                    onChange={(e) => setNewResourceData({ ...newResourceData, costAccount: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setCreateResourceModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg">Cancelar</button>
                            <button onClick={handleSaveNewResource} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg shadow-sm">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI ASSISTANT MODAL */}
            {aiAssistantModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className="bg-indigo-600 p-6 text-white flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-3xl">smart_toy</span>
                                <div>
                                    <h3 className="text-xl font-bold">Asistente de IA</h3>
                                    <p className="text-indigo-100 text-xs text-balance">Optimiza tu presupuesto con inteligencia artificial.</p>
                                </div>
                            </div>
                            <button onClick={() => setAiAssistantModalOpen(false)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {isAiProcessing ? (
                                <div className="py-8 flex flex-col items-center justify-center gap-4">
                                    <div className="size-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                    <p className="text-indigo-600 font-medium animate-pulse">La IA está procesando los datos...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-3">
                                        <button
                                            onClick={() => handleAiAction('analyze')}
                                            className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all text-left group"
                                        >
                                            <div className="size-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0 group-hover:scale-110 transition-transform">
                                                <span className="material-symbols-outlined">analytics</span>
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800 dark:text-slate-200">Análisis de Discrepancias</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">Encuentra precios que no coinciden con el mercado.</div>
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => handleAiAction('distribute')}
                                            className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all text-left group"
                                        >
                                            <div className="size-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 shrink-0 group-hover:scale-110 transition-transform">
                                                <span className="material-symbols-outlined">account_balance_wallet</span>
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800 dark:text-slate-200">Distribución Automática</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">Sugiere la distribución de montos según históricos.</div>
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => handleAiAction('omissions')}
                                            className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all text-left group"
                                        >
                                            <div className="size-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0 group-hover:scale-110 transition-transform">
                                                <span className="material-symbols-outlined">rule</span>
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800 dark:text-slate-200">Detección de Omisiones</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">Detecta posibles partidas faltantes en tu obra.</div>
                                            </div>
                                        </button>
                                    </div>

                                    <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-[10px] text-slate-500">
                                            <span className="material-symbols-outlined text-[14px]">info</span>
                                            <span>La IA utiliza el contexto actual de tu presupuesto para realizar estas acciones.</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BudgetScreen;